import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider
} from "@mui/material";
import {
  ArrowBack,
  DirectionsCar,
  LocalShipping,
  Person,
  PersonOutline,
  Phone,
  LocationOn,
  Home,
  ContactPhone,
  CheckCircle,
  Description,
  Event
} from "@mui/icons-material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "../context/PermissionContext";

const ApprovalDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission } = usePermissions();
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [approval, setApproval] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");

  const vehicleTypeMap = {
    1: "Motorcycle",
    2: "Car",
    3: "Tricycle",
    4: "Truck"
  };

  const statusColors = {
    pending: 'warning',
    approved: 'success',
    rejected: 'error'
  };

  useEffect(() => {
    fetchApprovalDetails();
  }, [id]);

  const fetchApprovalDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${baseUrl}/api/approvals/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApproval(response.data);
    } catch (error) {
      console.error("Error fetching approval details:", error);
      setError("Failed to load approval details");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem("token");
      await axios.put(
        `${baseUrl}/api/approvals/${id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchApprovalDetails();
    } catch (error) {
      console.error("Error approving:", error);
      setError(error.response?.data?.error || "Failed to approve");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    const reason = prompt("Please provide a reason for rejection:");
    if (!reason) return;

    try {
      setActionLoading(true);
      const token = localStorage.getItem("token");
      await axios.put(
        `${baseUrl}/api/approvals/${id}/reject`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchApprovalDetails();
    } catch (error) {
      console.error("Error rejecting:", error);
      setError(error.response?.data?.error || "Failed to reject");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/dashboard/approvals");
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !approval) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error || "Approval not found"}</Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>Back to Approvals</Button>
      </Container>
    );
  }

  const VehicleIcon = approval.vehicleType === 1 ? DirectionsCar : LocalShipping;

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Header Section */}
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' } }}>
                {approval.customId}
              </Typography>
              <Chip
                label={approval.status.toUpperCase()}
                color={statusColors[approval.status]}
                sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" gap={{ xs: 1, sm: 2 }} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<ArrowBack />}
                onClick={handleBack}
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}
              >
                Back
              </Button>
              {approval.status === 'pending' && hasPermission('approval', 'approve') && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleApprove}
                  disabled={actionLoading}
                  sx={{ backgroundColor: '#4CAF50', '&:hover': { backgroundColor: '#45a049' } }}
                >
                  Approve
                </Button>
              )}
              {approval.status === 'pending' && hasPermission('approval', 'reject') && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={handleReject}
                  disabled={actionLoading}
                  sx={{ backgroundColor: '#f44336', '&:hover': { backgroundColor: '#da190b' } }}
                >
                  Reject
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Quick Info Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ minHeight: 120, background: 'linear-gradient(135deg, #2196F3 0%, #64B5F6 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Vehicle Type
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {vehicleTypeMap[approval.vehicleType] || 'Unknown'}
                  </Typography>
                </Box>
                <VehicleIcon sx={{ fontSize: 48, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ minHeight: 120, background: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Owner
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" noWrap>
                    {`${approval.ofirstName} ${approval.olastName}`}
                  </Typography>
                </Box>
                <Person sx={{ fontSize: 48, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ minHeight: 120, background: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Operator
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" noWrap>
                    {approval.opfirstName ? `${approval.opfirstName} ${approval.oplastName}` : 'Not Assigned'}
                  </Typography>
                </Box>
                <PersonOutline sx={{ fontSize: 48, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ minHeight: 120, background: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Submitted
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {new Date(approval.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Event sx={{ fontSize: 48, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Owner and Operator Information Row */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
        {/* Owner Information */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, height: '100%' }}>
            <Typography variant="h5" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 }, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              <Person sx={{ mr: 1 }} />
              Owner Information
            </Typography>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
                {approval.ownerProfileImage ? (
                  <Box
                    component="img"
                    src={`${baseUrl}/${approval.ownerProfileImage}`}
                    alt="Owner Profile"
                    sx={{
                      width: { xs: 120, sm: 140, md: 150 },
                      height: { xs: 120, sm: 140, md: 150 },
                      mx: 'auto',
                      boxShadow: 3,
                      border: '4px solid #f0f0f0',
                      borderRadius: 2,
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <Box sx={{
                    width: { xs: 120, sm: 140, md: 150 },
                    height: { xs: 120, sm: 140, md: 150 },
                    mx: 'auto',
                    bgcolor: '#4CAF50',
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    color: 'white',
                    fontWeight: 'bold'
                  }}>
                    {approval.ofirstName?.[0]}
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={8}>
                <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, textAlign: { xs: 'center', sm: 'left' } }}>
                  {`${approval.ofirstName} ${approval.omiddleName || ''} ${approval.olastName}`.trim()}
                </Typography>
                <List dense>
                  <ListItem sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                    <ListItemIcon sx={{ minWidth: { xs: '100%', sm: '40px' }, mb: { xs: 0.5, sm: 0 } }}><Phone color="primary" /></ListItemIcon>
                    <ListItemText primary="Contact" secondary={approval.ocontact || 'Not provided'} />
                  </ListItem>
                  <ListItem sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                    <ListItemIcon sx={{ minWidth: { xs: '100%', sm: '40px' }, mb: { xs: 0.5, sm: 0 } }}><LocationOn color="primary" /></ListItemIcon>
                    <ListItemText primary="District" secondary={approval.odistrict || 'Not provided'} />
                  </ListItem>
                  <ListItem sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                    <ListItemIcon sx={{ minWidth: { xs: '100%', sm: '40px' }, mb: { xs: 0.5, sm: 0 } }}><Home color="primary" /></ListItemIcon>
                    <ListItemText primary="Address" secondary={approval.oaddress || 'Not provided'} />
                  </ListItem>
                  <ListItem sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                    <ListItemIcon sx={{ minWidth: { xs: '100%', sm: '40px' }, mb: { xs: 0.5, sm: 0 } }}><ContactPhone color="primary" /></ListItemIcon>
                    <ListItemText primary="Emergency Contact" secondary={approval.oemgContact || 'Not provided'} />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Operator Information */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, height: '100%' }}>
            <Typography variant="h5" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 }, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              <PersonOutline sx={{ mr: 1 }} />
              Operator Information
            </Typography>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
                {approval.operatorProfileImage ? (
                  <Box
                    component="img"
                    src={`${baseUrl}/${approval.operatorProfileImage}`}
                    alt="Operator Profile"
                    sx={{
                      width: { xs: 120, sm: 140, md: 150 },
                      height: { xs: 120, sm: 140, md: 150 },
                      mx: 'auto',
                      boxShadow: 3,
                      border: '4px solid #f0f0f0',
                      borderRadius: 2,
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <Box sx={{
                    width: { xs: 120, sm: 140, md: 150 },
                    height: { xs: 120, sm: 140, md: 150 },
                    mx: 'auto',
                    bgcolor: '#FF9800',
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    color: 'white',
                    fontWeight: 'bold'
                  }}>
                    {approval.opfirstName?.[0] || 'N'}
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={8}>
                <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, textAlign: { xs: 'center', sm: 'left' } }}>
                  {approval.opfirstName ? `${approval.opfirstName} ${approval.opmiddleName || ''} ${approval.oplastName}`.trim() : 'Not Assigned'}
                </Typography>
                <List dense>
                  <ListItem sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                    <ListItemIcon sx={{ minWidth: { xs: '100%', sm: '40px' }, mb: { xs: 0.5, sm: 0 } }}><Phone color="primary" /></ListItemIcon>
                    <ListItemText primary="Contact" secondary={approval.opContact || 'Not provided'} />
                  </ListItem>
                  <ListItem sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                    <ListItemIcon sx={{ minWidth: { xs: '100%', sm: '40px' }, mb: { xs: 0.5, sm: 0 } }}><ContactPhone color="primary" /></ListItemIcon>
                    <ListItemText primary="Emergency Contact" secondary={approval.opemgContact || 'Not provided'} />
                  </ListItem>
                  <ListItem sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                    <ListItemIcon sx={{ minWidth: { xs: '100%', sm: '40px' }, mb: { xs: 0.5, sm: 0 } }}><Description color="primary" /></ListItemIcon>
                    <ListItemText primary="Driver's License" secondary={approval.opdriverLicense || 'Not provided'} />
                  </ListItem>
                  <ListItem sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                    <ListItemIcon sx={{ minWidth: { xs: '100%', sm: '40px' }, mb: { xs: 0.5, sm: 0 } }}><Home color="primary" /></ListItemIcon>
                    <ListItemText primary="Address" secondary={approval.opAddress || 'Not provided'} />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Vehicle Specifications */}
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: 3, borderRadius: 2 }}>
        <Typography variant="h5" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          <DirectionsCar sx={{ mr: 1 }} />
          Vehicle Specifications
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Business Name:</Typography>
              <Typography variant="body1" fontWeight="500">{approval.vbusinessName || 'N/A'}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">VIN Number:</Typography>
              <Chip label={approval.vvinNo || 'N/A'} color="primary" variant="outlined" />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Engine Number:</Typography>
              <Chip label={approval.vengineNo || 'N/A'} color="primary" variant="outlined" />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">License Plate:</Typography>
              {approval.vlicensePlate && (
                <Box
                  sx={{
                    display: 'inline-block',
                    backgroundColor: 'white',
                    color: 'black',
                    padding: '8px 16px',
                    borderRadius: 1,
                    border: '3px solid #333',
                    fontFamily: 'monospace',
                    fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                    fontWeight: 'bold',
                    letterSpacing: '0.1em'
                  }}
                >
                  {approval.vlicensePlate}
                </Box>
              )}
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Model:</Typography>
              <Typography variant="body1" fontWeight="500">{approval.vmodel || 'N/A'}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Color:</Typography>
              <Typography variant="body1" fontWeight="500">{approval.vcolor || 'N/A'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Receipt Number:</Typography>
              <Typography variant="body1" fontWeight="500">{approval.vreceiptNo || 'N/A'}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">K Number:</Typography>
              <Typography variant="body1" fontWeight="500">{approval.vkNo || 'N/A'}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Vehicle ID:</Typography>
              <Typography variant="body1" fontWeight="500">{approval.vvehicleID || 'N/A'}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Seller Address:</Typography>
              <Typography variant="body1" fontWeight="500">{approval.vAddress || 'N/A'}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Seller Contact:</Typography>
              <Typography variant="body1" fontWeight="500">{approval.vsellerContact || 'N/A'}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Purchase Date:</Typography>
              <Typography variant="body1" fontWeight="500">
                {approval.vpurchasedDate ? new Date(approval.vpurchasedDate).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Approval Status */}
      {approval.status !== 'pending' && (
        <Alert severity={approval.status === 'approved' ? 'success' : 'error'} sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {approval.status === 'approved' ? 'Approved' : 'Rejected'}
          </Typography>
          {approval.status === 'approved' && (
            <>
              <Typography variant="body2">
                <strong>Approved by:</strong> {approval.approver?.firstname} {approval.approver?.lastname}
              </Typography>
              <Typography variant="body2">
                <strong>Approved at:</strong> {new Date(approval.approvedAt).toLocaleString()}
              </Typography>
            </>
          )}
          {approval.status === 'rejected' && (
            <>
              <Typography variant="body2">
                <strong>Rejected by:</strong> {approval.rejecter?.firstname} {approval.rejecter?.lastname}
              </Typography>
              <Typography variant="body2">
                <strong>Rejected at:</strong> {new Date(approval.rejectedAt).toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Reason:</strong> {approval.rejectionReason}
              </Typography>
            </>
          )}
        </Alert>
      )}
    </Container>
  );
};

export default ApprovalDetail;
